import {
  CSSProperties,
  forwardRef,
  KeyboardEvent,
  useCallback,
  useEffect,
  useState
} from "react";
import {
  useBjelinTranslations,
  useProductTranslations
} from "../../hooks/TranslationHooks";
import classNames from "classnames";
import { Button, Input, InputGroup, InputGroupText } from "reactstrap";
import FormattedPrice from "../FormattedPrice";
import { multiply } from "../../utils/NumberUtils";
import Icon from "../Icon";
import "./style.scss";

const PackageCalculator = forwardRef<
  HTMLInputElement,
  {
    id?: string;
    className?: string;
    style?: CSSProperties;
    m2PerPackage: number;
    pricePerPackage: number;
    stock: string;
    currency?: string;
    valuePkg?: number;
    valueM2?: number;
    valueCallback: (value: number) => void;
    onKeyDown: (e: KeyboardEvent) => void;
    b2b?: boolean;
    isBusiness?: boolean;
    compact?: boolean;
  }
>((props, ref) => {
  const {
    id,
    className,
    style,
    m2PerPackage,
    pricePerPackage,
    stock,
    currency,
    valuePkg = 0,
    valueM2 = 0,
    valueCallback,
    onKeyDown,
    b2b = false,
    isBusiness = false,
    compact = false
  } = props;
  const t = useBjelinTranslations();
  const tp = useProductTranslations();
  const stockInt = parseInt(stock) || 999;
  const [m2, setM2] = useState<number | undefined>(
    valuePkg ? multiply(valuePkg, m2PerPackage) : valueM2 ? valueM2 : undefined
  );
  const [pkg, _setPkg] = useState<number>(
    valuePkg ? valuePkg : valueM2 ? Math.ceil(valueM2 / m2PerPackage) : 0
  );
  const setPkg = useCallback(
    (newPkg: number) => {
      _setPkg(newPkg);
      valueCallback?.(newPkg);
    },
    [valueCallback]
  );

  const changeM2 = (value: string) => {
    if (value.substr(-1) === "." || value.substr(-1) === ",") {
      setM2(parseFloat(value));
      return;
    }
    value = value.replace(",", "."); //Handle , as decimal sign
    let newM2: number | undefined = parseFloat(value);
    if (isNaN(newM2) || newM2 <= 0) {
      newM2 = undefined;
    }
    setM2(newM2);
    setPkg(Math.ceil((newM2 as number) / m2PerPackage));
  };

  const changePkg = (value: number) => {
    let newPkg = value;
    if (isNaN(newPkg) || newPkg < 1) {
      newPkg = 1;
    }
    setPkg(newPkg);
    setM2(multiply(newPkg, m2PerPackage));
  };

  //Make it possible to update m2 value from props
  useEffect(() => {
    setM2(
      valuePkg
        ? multiply(valuePkg, m2PerPackage)
        : valueM2
        ? valueM2
        : undefined
    );
    setPkg(
      valuePkg ? valuePkg : valueM2 ? Math.ceil(valueM2 / m2PerPackage) : 0
    );
  }, [m2PerPackage, setPkg, valueM2, valuePkg]);

  const total = multiply(pkg, pricePerPackage);

  return (
    <div
      id={id}
      className={classNames("package-calculator", className)}
      style={style}
    >
      {!compact ? (
        <>
          <div className={"plus-minus-box"}>
            <Button
              className={"decrease-cart-btn"}
              aria-label={tp("form-label.decrease.pkg")}
              onClick={() => pkg > 0 && changePkg(pkg - 1)}
              disabled={pkg <= 1}
              color="bjelin-darkgrey"
              style={{
                marginRight: 0,
                paddingLeft: "1rem",
                borderTopLeftRadius: "50%",
                borderBottomLeftRadius: "50%",
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0
              }}
            >
              <Icon name="minus" />
            </Button>
            <div className={"plus-minus-inner"}>
              <div className={"plus-minus-inner-inner"}>
                <InputGroup>
                  <span style={{ padding: "0.5rem" }}>
                    {pkg + " " + tp("sales_unit.per_square_meter") + " ="}
                  </span>
                </InputGroup>
                <InputGroup>
                  <Input
                    aria-label={tp("form-label.sqm")}
                    type="text"
                    inputMode="decimal"
                    min="0"
                    max="9999"
                    pattern="^\d*(\.\d{0,2})?$"
                    value={m2 || ""}
                    onChange={e => changeM2(e.target.value)}
                    onKeyDown={e => {
                      onKeyDown?.(e);
                    }}
                    onClick={e => {
                      e.stopPropagation();
                    }}
                    innerRef={ref}
                    style={{ width: "5em" }}
                  />
                  <InputGroupText>m&sup2;</InputGroupText>
                </InputGroup>
              </div>
            </div>
            <Button
              className={"increase-cart-btn"}
              aria-label={tp("form-label.increase.pkg")}
              onClick={() => changePkg(pkg + 1)}
              disabled={pkg >= stockInt}
              color="bjelin-darkgrey"
              style={{
                marginLeft: 0,
                paddingRight: "1rem",
                borderTopRightRadius: "50%",
                borderBottomRightRadius: "50%",
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0
              }}
            >
              <Icon name="plus" />
            </Button>
          </div>
          {!b2b && !isBusiness && !compact && (
            <div
              style={{
                marginTop: "1rem",
                fontSize: "1.25em",
                letterSpacing: "-0.02em"
              }}
            >
              <strong>
                {tp("sum")}:{" "}
                <FormattedPrice price={total} currency={currency} />
              </strong>
            </div>
          )}
        </>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "baseline" }}>
            <Input
              type="text"
              inputMode="decimal"
              min="0"
              max="9999"
              pattern="^\d*(\.\d{0,2})?$"
              value={m2 || ""}
              onChange={e => changeM2(e.target.value)}
              onKeyDown={e => {
                onKeyDown?.(e);
              }}
              onClick={e => {
                e.stopPropagation();
              }}
              innerRef={ref}
              style={{ width: "4em", marginRight: "0.5rem" }}
            />
            <span>m&sup2;</span>
          </div>
          <span className="text-small" style={{ marginLeft: "0.5rem" }}>
            {pkg > 0 && "= " + pkg + " " + tp("sales_unit.per_square_meter")}
          </span>
        </>
      )}
    </div>
  );
});

PackageCalculator.displayName = "PackageCalculator";

export default PackageCalculator;
